import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Image, DollarSign, Ruler, Clock } from "lucide-react";
import { api } from "@/lib/api";

const ISSUE_META = {
  stuck_in_prep: { label: "Stuck In Prep", icon: Clock, color: "text-amber-600 bg-amber-50" },
  missing_photos: { label: "Missing Photos", icon: Image, color: "text-rose-600 bg-rose-50" },
  unverified_rrp: { label: "Unverified RRP", icon: DollarSign, color: "text-amber-600 bg-amber-50" },
  missing_measurements: { label: "Missing Measurements", icon: Ruler, color: "text-zinc-600 bg-zinc-100" },
};

const SEVERITY_STYLE = {
  high: "bg-rose-100 text-rose-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-zinc-100 text-zinc-600",
};

export default function AdminAttention() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/attention-queue").then((res) => setData(res.data));
  }, []);

  if (!data) return <div className="p-10 text-zinc-400" data-testid="attention-loading">Loading attention queue…</div>;

  const tabs = [
    { key: "all", label: "All", count: data.total },
    { key: "high", label: "High", count: data.counts.high },
    { key: "medium", label: "Medium", count: data.counts.medium },
    { key: "low", label: "Low", count: data.counts.low },
  ];

  const filtered = filter === "all" ? data.items : data.items.filter((i) => i.severity === filter);

  return (
    <div className="p-8" data-testid="admin-attention-page">
      <div className="flex items-center gap-3 mb-2">
        <AlertTriangle size={24} className="text-amber-500" />
        <h1 className="font-serif text-3xl text-zinc-900">Attention Queue</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-8">Listings that need staff action before they're fully ready for sale — stuck in prep, missing photos, unverified RRP evidence, or missing measurements.</p>

      <div className="flex gap-2 mb-6" data-testid="attention-filter-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`px-4 py-2 rounded-full text-xs font-heading font-bold uppercase tracking-wide transition-colors ${
              filter === t.key ? "bg-teal-600 text-white" : "bg-white border border-zinc-200 text-zinc-500 hover:border-teal-300"
            }`}
            data-testid={`attention-filter-${t.key}`}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {filtered.length === 0 && data.total === 0 && (
        <p className="text-sm text-zinc-400 py-6" data-testid="attention-all-clear">All caught up — no listings currently need attention.</p>
      )}

      <div className="space-y-2" data-testid="attention-items-list">
        {filtered.map((item) => {
          const meta = ISSUE_META[item.issue_type] || { label: item.issue_type, icon: AlertTriangle, color: "text-zinc-600 bg-zinc-100" };
          const Icon = meta.icon;
          return (
            <Link
              key={item.id}
              to={item.link}
              className="flex items-center gap-4 bg-white border border-zinc-200 rounded-xl p-4 hover:border-teal-400 transition-colors"
              data-testid={`attention-item-${item.id}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${meta.color}`}>
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-800">{item.message}</div>
                <div className="text-[11px] font-heading uppercase tracking-wide text-zinc-400 mt-0.5">{meta.label}</div>
              </div>
              <span className={`text-[10px] font-heading font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shrink-0 ${SEVERITY_STYLE[item.severity] || SEVERITY_STYLE.low}`}>
                {item.severity}
              </span>
            </Link>
          );
        })}
      </div>

      {filtered.length === 0 && data.total > 0 && (
        <p className="text-sm text-zinc-400 py-6" data-testid="attention-empty-filtered">No items match this filter.</p>
      )}
    </div>
  );
}
